import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { PromptLibraryListComponent } from './prompt-library-list/prompt-library-list.component';
import { PromptLibraryService } from '../services/prompt-library.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { IInstructions } from '../../automation/interfaces/i-instructions';
import { IWorkflow } from '../../automation/interfaces/i-workflow';
import { ChatSessionService } from '../services/chat-session.service';
import { ISensitivityLabel } from '../../../interfaces/i-sensitivity-label';
import { ILibraryItem } from './interfaces/i-library-item';
import { MultiSessionService } from '../../../services/multi-session.service';
import { isSensitivityLabelEqualOrLower } from '../../../utilities/sensitivity-label.utils';


/**
 * Component that houses the Automation Library drawer
 */
@Component({
  selector: 'app-prompt-library',
  imports: [
    MatIcon,
    MatButtonModule,
    MatTooltipModule,
    MatInputModule,
    FormsModule,
    PromptLibraryListComponent,
  ],
  templateUrl: './prompt-library.component.html',
  styleUrls: ['./prompt-library.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromptLibraryComponent {
  tooltipText = 'To add a Prompt, you can either create a new Instruction or Workflow, or click on the Save icon in any User sent prompt in the chat session. To run an Automation, click on the Automation name.';

  // Raw search term signal capturing user input
  searchTerm = signal('');

  // Debounced search term (500ms delay) via RxJS
  private readonly debouncedSearchTerm = toSignal(
    toObservable(this.searchTerm).pipe(
      debounceTime(500),
      distinctUntilChanged()
    ),
    { initialValue: '' }
  );

  // Filtered data for the Automation Library
  instructions = signal<ILibraryItem[]>([]);
  workflows = signal<ILibraryItem[]>([]);

  /** Controls whether the Instructions section is expanded. */
  protected readonly instructionsOpen = signal(false);
  /** Controls whether the Workflows section is expanded. */
  protected readonly workflowsOpen = signal(false);

  // Original data from the service
  #originalInstructions: IInstructions[] = [];
  #originalWorkflows: IWorkflow[] = [];
  #promptLibraryService = inject(PromptLibraryService);
  #chatSessionService = inject(ChatSessionService);
  #multiSessionService = inject(MultiSessionService);

  // Computed signal for global automation state
  isAutomationActive = computed(() =>
    this.#multiSessionService.hasReachedMaxConcurrency() || this.#multiSessionService.isSessionStreaming(this.#chatSessionService.selectedSessionId())
  );

  constructor() {
    this.#promptLibraryService.loadWorkflows();
    this.#promptLibraryService.loadInstructions();

    // React to all dependencies that affect filtering: service data, selected session, and debounced search term
    effect(() => {
      // Track all dependencies
      const instructions = this.#promptLibraryService.instructions();
      const workflows = this.#promptLibraryService.workflows();
      this.#chatSessionService.selectedSession();
      this.debouncedSearchTerm();

      // Guard: only proceed if we have data loaded
      if (instructions.length === 0 && workflows.length === 0) {
        return;
      }

      // Update original data sources
      this.#originalInstructions = instructions;
      this.#originalWorkflows = workflows;

      // Single filter operation for all dependency changes
      this.filterPrompts();
    });
  }

  /** Toggle the Instructions section open/closed. */
  protected toggleInstructions(): void {
    this.instructionsOpen.update(open => !open);
  }

  /** Toggle the Workflows section open/closed. */
  protected toggleWorkflows(): void {
    this.workflowsOpen.update(open => !open);
  }

   /**
    * Filter prompts based on search term and sensitivity labels from the current session
    * This implementation filters by name, description, and sensitivity labels in both instructions and workflows
    * It also marks items as disabled if they don't have matching sensitivity labels and sorts them accordingly
    * Auto-expands sections that have search results
    */
   filterPrompts(): void {
     // Get current session sensitivity labels
     const sessionSensitivityLabels = this.#chatSessionService.selectedSession()?.sensitivityLabels || [];

     // Create ILibraryItem arrays from original data with sensitivity check
     const instructionItems = this.#createLibraryItems(
       this.#originalInstructions,
       sessionSensitivityLabels,
       'instruction'
     );

     const workflowItems = this.#createLibraryItems(
       this.#originalWorkflows,
       sessionSensitivityLabels,
       'workflow'
     );

     // Use the debounced search term to filter with the 500ms delay applied
     const currentSearchTerm = this.debouncedSearchTerm();

     // If no search term, use all data with appropriate disabled flags
     if (!currentSearchTerm || currentSearchTerm.trim() === '') {
       this.instructions.set(instructionItems);
       this.workflows.set(workflowItems);
       // Collapse sections when search is cleared
       this.instructionsOpen.set(false);
       this.workflowsOpen.set(false);
       return;
     }

     // If there's a search term, apply text filtering
     const term = currentSearchTerm.toLowerCase().trim();

     // Filter instructions by text
     const filteredInstructions = instructionItems.filter(libItem => {
       const instruction = libItem.item as IInstructions;
       return instruction.details.name.toLowerCase().includes(term) ||
         instruction.details.prompt.toLowerCase().includes(term) ||
         instruction.details.description?.toLowerCase().includes(term);
     });

     // Filter workflows by text
     const filteredWorkflows = workflowItems.filter(libItem => {
       const workflow = libItem.item as IWorkflow;
       return workflow.details.name.toLowerCase().includes(term) ||
         workflow.details.description?.toLowerCase().includes(term);
     });

     // Update the signals
     this.instructions.set(filteredInstructions);
     this.workflows.set(filteredWorkflows);

     // Auto-expand sections that have results
     this.instructionsOpen.set(filteredInstructions.length > 0);
     this.workflowsOpen.set(filteredWorkflows.length > 0);
   }

  /**
   * Creates library items with disabled state based on sensitivity label matching
   * @private
   * @param items Items to process (instructions or workflows)
   * @param sessionLabels Current session's sensitivity labels
   * @param type Type of item ('instruction' or 'workflow')
   * @returns Array of ILibraryItem with appropriate disabled flags, sorted with enabled items first
   */
  #createLibraryItems<T extends IInstructions | IWorkflow>(
    items: T[],
    sessionLabels: ISensitivityLabel[],
    type: 'instruction' | 'workflow'
  ): ILibraryItem[] {
    // Map items to ILibraryItem with disabled state
    const sessionLabel = sessionLabels[0];
    const libraryItems = items.map(item => {
      // Get item's sensitivity labels
      const itemLabels = item.details.sensitivityLabels || [];

      // Item is disabled when the session's sensitivity label is lower than the item's.
      // Compared by canonical sensitivity rank (not exact code/guid equality), consistent
      // with document filtering. Multi-label items use "least restrictive wins": the item
      // is allowed when at least one of its labels is equal-or-lower sensitivity than the
      // session's label (matches the agent filter and platform min-rank semantics).
      // TODO: revisit multi-label policy — decide whether the lowest or highest label
      // should govern when an item carries competing sensitivity labels.
      // If there's no session label, or the item has no labels, the item stays enabled.
      const isDisabled = !!sessionLabel
        && itemLabels.length > 0
        && !itemLabels.some(itemLabel => isSensitivityLabelEqualOrLower(itemLabel, sessionLabel));

      return {
        item,
        isDisabled,
        type
      };
    });

    // Sort: enabled items first, then disabled items
    return libraryItems.sort((a, b) => {
      // First sort by disabled state
      if (a.isDisabled !== b.isDisabled) {
        return a.isDisabled ? 1 : -1; // enabled items first
      }

      // If both have the same disabled state, sort alphabetically by name
      const nameA = a.item.details.name.toLowerCase();
      const nameB = b.item.details.name.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }

  /**
   * Clear the search term and let the reactive effect handle filtering
   */
  clearSearch(): void {
    this.searchTerm.set('');
  }
}
